import React, { memo, useId } from 'react';

/**
 * Векторный набор интерфейсных иконок Tap Mars.
 *
 * Раньше навигация, заголовки и карточки рисовались PNG-эмодзи из
 * /assets/icons — они выглядят по-разному на iOS/Android/Desktop, не держат
 * единую палитру и ломают ритм сетки (разные оптические размеры и поля).
 * Здесь тот же язык, что у RubyIcon/StarIcon: силуэт на сетке 32×32,
 * градиентная заливка марсианской палитрой, внутренняя тень и мягкий ореол.
 *
 * Все иконки рисуются в одном оптическом весе, поэтому строка «иконка + текст»
 * выравнивается одинаково на любом экране.
 */

/* Палитры: [светлый блик, основной, тень] */
const PALETTES = {
  ember: ['#ffd9a8', '#ff8b3d', '#c23b02'],
  gold: ['#fff3c4', '#ffc34d', '#c47a06'],
  ruby: ['#ffd7e0', '#ff4d6d', '#8d0b31'],
  ice: ['#e8f6ff', '#7fd4ff', '#1f6d9c'],
  dust: ['#fff0e2', '#e0b48c', '#8a5a36'],
  steel: ['#eef2f7', '#b9c4d2', '#5b6675'],
};

/** Геометрия каждой иконки: тело (заливка градиентом) + детали (штрих). */
const SHAPES = {
  home: {
    tone: 'ember',
    body: 'M16 4.2 29 15.1a1.6 1.6 0 0 1-1 2.8h-1.9v8.4a1.7 1.7 0 0 1-1.7 1.7H8.6a1.7 1.7 0 0 1-1.7-1.7v-8.4H5a1.6 1.6 0 0 1-1-2.8L16 4.2z',
    line: 'M12.8 27.9v-7.2h6.4v7.2',
  },
  toolbox: {
    tone: 'ember',
    body: 'M4.4 12.4h23.2a1.7 1.7 0 0 1 1.7 1.7v11a1.7 1.7 0 0 1-1.7 1.7H4.4a1.7 1.7 0 0 1-1.7-1.7v-11a1.7 1.7 0 0 1 1.7-1.7z',
    line: 'M11.4 12.4V8.6a2 2 0 0 1 2-2h5.2a2 2 0 0 1 2 2v3.8M2.7 18.4h26.6M13.6 16.2h4.8',
  },
  crown: {
    tone: 'gold',
    body: 'M4.6 24.2 2.4 9.6l7.6 5.2L16 5.4l6 9.4 7.6-5.2-2.2 14.6a1.5 1.5 0 0 1-1.5 1.3H6.1a1.5 1.5 0 0 1-1.5-1.3z',
    line: 'M5.6 27.4h20.8',
    dots: [[16, 12.4, 1.5], [7.4, 15.6, 1.1], [24.6, 15.6, 1.1]],
  },
  trophy: {
    tone: 'gold',
    // Чаша, ножка и основание — один силуэт: если ножку рисовать штрихом, на
    // 24px кубок теряет талию и читается как щит.
    body: 'M8.6 3.6h14.8v7.6c0 3.6-2.4 6.6-5.6 7.4v3.2h3.4a1 1 0 0 1 1 1v1.4h-12.4v-1.4a1 1 0 0 1 1-1h3.4v-3.2c-3.2-.8-5.6-3.8-5.6-7.4V3.6z',
    line: 'M8.6 6.4H5.2a1.5 1.5 0 0 0-1.5 1.5c0 3.5 2.5 6.1 5.5 6.5M23.4 6.4h3.4a1.5 1.5 0 0 1 1.5 1.5c0 3.5-2.5 6.1-5.5 6.5M9.6 27.2h12.8',
  },
  folder: {
    tone: 'dust',
    body: 'M3.4 8.6a1.7 1.7 0 0 1 1.7-1.7h7l3 3.4h10.8a1.7 1.7 0 0 1 1.7 1.7v13.4a1.7 1.7 0 0 1-1.7 1.7H5.1a1.7 1.7 0 0 1-1.7-1.7V8.6z',
    line: 'M3.4 14.8h25.2',
  },
  handshake: {
    tone: 'ember',
    // Метафора «двое» вместо рукопожатия: сцепленные ладони на 20px
    // превращаются в кляксу, две фигуры читаются мгновенно.
    body: 'M11.4 5.2a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8zM3.4 27.4v-1.8c0-3.8 3.6-6.6 8-6.6s8 2.8 8 6.6v1.8H3.4z',
    line: 'M21.2 7.6a3.6 3.6 0 0 1 0 7.2M22.6 20.2c3.4.7 6 3 6 5.8v1.4h-5.4',
  },
  bulb: {
    tone: 'gold',
    body: 'M16 3.4a9 9 0 0 1 5.6 16.1c-.9.8-1.4 1.7-1.4 2.7v.6h-8.4v-.6c0-1-.5-1.9-1.4-2.7A9 9 0 0 1 16 3.4z',
    line: 'M12.2 25.4h7.6M13.4 28.4h5.2',
  },
  chart_up: {
    tone: 'ice',
    body: 'M4 26.4 11.6 17l5 4.4L27 8.6l1.6 1.4-11.6 14.4-5-4.4-6.4 7.8z',
    line: 'M20.4 8.6h8v8',
  },
  muscle: {
    tone: 'ember',
    // Удар: ядро и разлетающиеся лучи. Иконка стоит на «силе тапа» и на
    // «эффектах тапа» — попытки нарисовать бицепс или кулак на 20px давали
    // нечитаемое пятно, а импульс узнаётся сразу.
    // Ядро — круг, а не звезда: звезда уже занята «престижем».
    body: 'M16 9.4a6.6 6.6 0 1 1 0 13.2 6.6 6.6 0 0 1 0-13.2z',
    line: 'M16 4.4V1.6M6.4 8 4.3 5.9M25.6 8l2.1-2.1M4.6 19.4 2 20.4M27.4 19.4l2.6 1M11.4 27.6l-1 2.6M20.6 27.6l1 2.6',
  },
  fire: {
    tone: 'ember',
    body: 'M16 2.6c5 5.2 9.4 8.4 9.4 14.4A9.4 9.4 0 0 1 16 29.4a9.4 9.4 0 0 1-9.4-12.4c0-3.4 1.6-5.4 3.6-7.6.2 2.4 1 3.8 2.4 4.6 1-3.4 1.6-7.4 3.4-11.4z',
    line: null,
    inner: 'M16 29.4a4.6 4.6 0 0 1-4.6-4.8c0-2.6 2.4-4 4.6-8 2.2 4 4.6 5.4 4.6 8a4.6 4.6 0 0 1-4.6 4.8z',
  },
  glowing_star: {
    tone: 'gold',
    body: 'M16 2.2 19.6 10.4 28.5 11.2 21.8 17.1 23.9 25.8 16 21.4 8.1 25.8 10.2 17.1 3.5 11.2 12.4 10.4 16 2.2z',
    line: null,
    dots: [[27, 5, 1.1], [5.4, 25.6, 0.9]],
  },
  gem: {
    tone: 'ruby',
    body: 'M16 3 28.5 11.6 16 29.4 3.5 11.6 16 3z',
    line: 'M3.5 11.6h25M10.6 11.6 16 29.4l5.4-17.8M10.6 11.6 16 3l5.4 8.6',
  },
  rocket: {
    tone: 'ice',
    // Корпус вместе с килями: раздельные штрихи на мелком размере расползались.
    body: 'M16 2.2c4.4 3.8 6.8 8.6 6.8 14.4 0 1.6-.2 3.1-.6 4.6l3.4 3.2-1.2-6 2.4 2.6-.6 5.6-4.8-2.6-1 1.4h-8.8l-1-1.4-4.8 2.6-.6-5.6 2.4-2.6-1.2 6 3.4-3.2c-.4-1.5-.6-3-.6-4.6C9.2 10.8 11.6 6 16 2.2z',
    line: 'M13.6 26.6h4.8l-2.4 3.2-2.4-3.2z',
    dots: [[16, 12.4, 2.8]],
  },
  question: {
    tone: 'dust',
    body: 'M16 2.8A13.2 13.2 0 1 1 16 29.2 13.2 13.2 0 0 1 16 2.8z',
    line: 'M12.4 12.6a3.7 3.7 0 0 1 7.2 1.2c0 2.6-3.6 3-3.6 5.4v.8',
    dots: [[16, 24.2, 1.5]],
    hollow: true,
  },
  energy_new: {
    tone: 'gold',
    body: 'M18.4 2.4 7.2 18.2h6.6L12.8 29.6 24.8 13H17.6l.8-10.6z',
    line: null,
  },
  /* coin.png сюда намеренно не входит: это отрисованная планета — авторский
     арт валюты, а не эмодзи, и вектор его только упростит. */
  cross: {
    tone: 'steel',
    body: 'M9.4 7.2 16 13.8l6.6-6.6 2.2 2.2-6.6 6.6 6.6 6.6-2.2 2.2-6.6-6.6-6.6 6.6-2.2-2.2 6.6-6.6-6.6-6.6 2.2-2.2z',
    line: null,
  },
  button: {
    tone: 'ember',
    body: 'M16 3.2a12.8 12.8 0 1 1 0 25.6 12.8 12.8 0 0 1 0-25.6z',
    line: 'M16 8.4v7.2',
    dots: [[16, 20.4, 2.2]],
  },
};

/** Старые имена PNG → ключи набора. */
const ALIASES = {
  'energy.png': 'energy_new',
  'star.png': 'glowing_star',
  'ruby.png': 'gem',
};

export function glyphName(src) {
  if (typeof src !== 'string') return null;
  const file = src.split('?')[0].split('/').pop();
  if (!file) return null;
  const key = ALIASES[file] || file.replace(/\.(png|svg|gif)$/i, '');
  return SHAPES[key] ? key : null;
}

/** Есть ли для этого PNG векторная замена. */
export function hasGlyph(src) {
  return glyphName(src) !== null;
}

const Glyph = memo(function Glyph({ name, size = 28, className = '', title = '' }) {
  const key = SHAPES[name] ? name : glyphName(name);
  const uid = useId().replace(/:/g, '');
  if (!key) return null;

  const shape = SHAPES[key];
  const [light, mid, dark] = PALETTES[shape.tone] || PALETTES.ember;
  const fill = `gl-f-${uid}`;
  const glow = `gl-g-${uid}`;

  return (
    <span
      className={`fx-icon glyph glyph-${key} ${className}`}
      style={{ width: size, height: size }}
      role={title ? 'img' : undefined}
      aria-label={title || undefined}
      aria-hidden={title ? undefined : true}
    >
      <svg viewBox="0 0 32 32" className="fx-icon-svg glyph-svg">
        <defs>
          <linearGradient id={fill} x1="18%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor={light} />
            <stop offset="46%" stopColor={mid} />
            <stop offset="100%" stopColor={dark} />
          </linearGradient>
          <filter id={glow} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="0.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g filter={`url(#${glow})`}>
          <path
            d={shape.body}
            fill={shape.hollow ? 'rgba(255,255,255,0.06)' : `url(#${fill})`}
            stroke={shape.hollow ? mid : dark}
            strokeWidth={shape.hollow ? 1.8 : 0.9}
            strokeLinejoin="round"
          />
          {/* верхний блик — объём без второго градиента */}
          <path d={shape.body} fill="none" stroke="rgba(255,255,255,0.42)" strokeWidth="0.8" strokeLinejoin="round" />
          {shape.inner ? <path d={shape.inner} fill="rgba(255,240,200,0.9)" /> : null}
          {shape.line ? (
            /* Штрих в два прохода. Детали вроде ручек кубка выходят за силуэт:
               одним тёмным штрихом они пропадали на тёмном фоне, одним светлым —
               на светлой заливке тела. Тёмная подложка + светлая линия поверх
               читаются и там, и там. */
            <>
              <path
                d={shape.line}
                fill="none"
                stroke="rgba(20,6,2,0.7)"
                strokeWidth={shape.hollow ? 3.4 : 3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={shape.line}
                fill="none"
                stroke={shape.hollow ? mid : light}
                strokeWidth={shape.hollow ? 2 : 1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : null}
          {(shape.dots || []).map(([cx, cy, r], i) => (
            <circle key={i} cx={cx} cy={cy} r={r} fill={shape.hollow ? mid : light} />
          ))}
        </g>
      </svg>
    </span>
  );
});

export default Glyph;

/* Металлы призовых мест — те же три тона, что у рамок в топе. */
const MEDALS = {
  1: { light: '#fff6cf', mid: '#ffcc4d', dark: '#a86a04', ribbon: '#c9302c' },
  2: { light: '#ffffff', mid: '#d7dee8', dark: '#7d8894', ribbon: '#3f6ea8' },
  3: { light: '#ffdcc0', mid: '#e08a4d', dark: '#8f4a1c', ribbon: '#5a6b7a' },
};

/**
 * Медаль призёра. Раньше здесь стояли 🥇/🥈/🥉 и 👑 — у эмодзи разная высота
 * ленты на разных платформах, из-за чего аватар в пьедестале «прыгал».
 * Вектор держит одну базовую линию: диск всегда в одном месте бокса.
 */
export const Medal = memo(function Medal({ place = 1, size = 26, crown = false, className = '' }) {
  const uid = useId().replace(/:/g, '');
  const m = MEDALS[place] || MEDALS[3];
  const disc = `md-d-${uid}`;
  const glow = `md-g-${uid}`;

  return (
    <span
      className={`fx-icon glyph medal medal-${place} ${className}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg viewBox="0 0 32 32" className="fx-icon-svg glyph-svg">
        <defs>
          <linearGradient id={disc} x1="20%" y1="0%" x2="80%" y2="100%">
            <stop offset="0%" stopColor={m.light} />
            <stop offset="48%" stopColor={m.mid} />
            <stop offset="100%" stopColor={m.dark} />
          </linearGradient>
          <filter id={glow} x="-45%" y="-45%" width="190%" height="190%">
            <feGaussianBlur stdDeviation="0.75" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g filter={`url(#${glow})`}>
          {crown ? (
            <>
              <path
                d="M4.8 23.4 2.6 9.2l7.5 5.1L16 5.2l5.9 9.1 7.5-5.1-2.2 14.2a1.5 1.5 0 0 1-1.5 1.3H6.3a1.5 1.5 0 0 1-1.5-1.3z"
                fill={`url(#${disc})`}
                stroke={m.dark}
                strokeWidth="0.9"
                strokeLinejoin="round"
              />
              <path d="M5.8 27.2h20.4" stroke={m.dark} strokeWidth="2.2" strokeLinecap="round" fill="none" />
              <circle cx="16" cy="12.2" r="1.6" fill={m.light} />
              <circle cx="7.6" cy="15.2" r="1.2" fill={m.light} />
              <circle cx="24.4" cy="15.2" r="1.2" fill={m.light} />
            </>
          ) : (
            <>
              {/* лента — две полосы под диском, симметрично */}
              <path d="M10.6 2.6 15 12.4h-4.4L6.2 2.6h4.4z" fill={m.ribbon} />
              <path d="M21.4 2.6 17 12.4h4.4L25.8 2.6h-4.4z" fill={m.ribbon} opacity="0.75" />
              <circle cx="16" cy="20.4" r="9.2" fill={`url(#${disc})`} stroke={m.dark} strokeWidth="0.9" />
              <circle cx="16" cy="20.4" r="6.4" fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.9" />
              <text
                x="16"
                y="24.2"
                textAnchor="middle"
                fontSize="8.6"
                fontWeight="800"
                fontFamily="Oxanium, sans-serif"
                fill="rgba(30,12,2,0.78)"
              >
                {place}
              </text>
            </>
          )}
        </g>
      </svg>
    </span>
  );
});
